import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  SelectChangeEvent,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';

interface FormData {
  mahalle: string;
  sokak: string;
  binaNo: string;
  daireNo: string;
  kat: string;
  alan: string;
  binaYasi: string;
  malikAdi: string;
  tcKimlik: string;
}

const MAHALLELER = [
  "15 Temmuz",
  "Birlik",
  "Çifte Havuzlar",
  "Davutpaşa",
  "Fatih",
  "Fevzi Çakmak",
  "Havaalanı",
  "Kazım Karabekir",
  "Kemer",
  "Menderes",
  "Mimar Sinan",
  "Namık Kemal",
  "Nine Hatun",
  "Oruçreis",
  "Tuna",
  "Turgut Reis",
  "Yavuz Selim"
];

const TransformationRequest: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    mahalle: '',
    sokak: '',
    binaNo: '',
    daireNo: '',
    kat: '',
    alan: '',
    binaYasi: '',
    malikAdi: '',
    tcKimlik: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Form verilerini API'ye gönder
      const response = await fetch('http://localhost:8000/transformation-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mahalle: formData.mahalle,
          sokak: formData.sokak,
          bina_no: formData.binaNo,
          daire_no: formData.daireNo,
          kat: formData.kat,
          alan: formData.alan,
          bina_yasi: formData.binaYasi,
          malik_adi: formData.malikAdi,
          tc_kimlik: formData.tcKimlik
        }),
      });

      if (!response.ok) {
        throw new Error('Başvuru gönderilirken bir hata oluştu.');
      }

      // Başarılı senaryo
      setSuccess(true);
      setFormData({
        mahalle: '',
        sokak: '',
        binaNo: '',
        daireNo: '',
        kat: '',
        alan: '',
        binaYasi: '',
        malikAdi: '',
        tcKimlik: '',
      });
    } catch (err) {
      setError('Bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        maxWidth: 1200, 
        mx: 'auto',
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderImage: 'linear-gradient(90deg, rgb(248, 153, 49), #424242) 1',
        boxShadow: '0 4px 24px 0 rgba(248, 153, 49, 0.10)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', borderRadius: 2 }} />
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Başvurunuz başarıyla alındı. E-devlet üzerinden kimlik doğrulaması yapıldıktan sonra size bilgi verilecektir.
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon color="primary" />
              Adres Bilgileri
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mahalle</InputLabel>
              <Select
                name="mahalle"
                value={formData.mahalle}
                onChange={handleSelectChange}
                label="Mahalle"
                required
              >
                {MAHALLELER.map((mahalle) => (
                  <MenuItem key={mahalle} value={mahalle}>{mahalle} Mahallesi</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Sokak"
              name="sokak"
              value={formData.sokak}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Bina No"
                name="binaNo"
                value={formData.binaNo}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Daire No"
                name="daireNo"
                value={formData.daireNo}
                onChange={handleInputChange}
                required
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HomeIcon color="primary" />
              Bina Bilgileri
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Kat"
                name="kat"
                value={formData.kat}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Alan (m²)"
                name="alan"
                value={formData.alan}
                onChange={handleInputChange}
                required
                type="number"
              />
            </Box>
            <TextField
              fullWidth
              label="Bina Yaşı"
              name="binaYasi"
              value={formData.binaYasi}
              onChange={handleInputChange}
              required
              type="number"
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Malik Bilgileri
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Malik Adı"
              name="malikAdi"
              value={formData.malikAdi}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="T.C. Kimlik No"
              name="tcKimlik"
              value={formData.tcKimlik}
              onChange={handleInputChange}
              required
              inputProps={{ maxLength: 11, pattern: '[0-9]*' }}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              minWidth: 200,
              py: 1.5,
              borderRadius: 2,
              background: 'rgb(248, 153, 49)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '1.1rem',
              boxShadow: '0 3px 15px rgba(248, 153, 49, 0.18)',
              '&:hover': {
                background: 'rgb(198, 123, 39)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Başvuru Yap'
            )}
          </Button>
        </Box>
      </form>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        * Bu form üzerinden yapılan başvurular Esenler Belediyesi tarafından değerlendirilecektir.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        * E-devlet üzerinden kimlik doğrulaması yapılacaktır.
      </Typography>
    </Paper>
  );
};

export default TransformationRequest; 